import React, { useState, useEffect, useRef } from 'react';
import firebase, { db, checkStorageLimit } from 'src/config/firebase';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TextField } from '@mui/material';
import { nanoid } from 'nanoid';
import { useCompanyData } from '../../shared/contexts/DataContext';
import CustomButton from '../components/CustomButton';
import { CircularProgress, Backdrop } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { ArrowForward, ArrowBack, AddCircle } from '@mui/icons-material';
import { AlertMessage } from 'src/screens/Partners/Service/components';
import { SimpleHeader } from './Contact';
import deleteStorageFile from 'src/config/DeleteFile';
import CompressImage from 'src/hooks/CompressImage';

const ColorCircularProgress = withStyles({
  root: {
    color: '#0F467A',
  },
})(CircularProgress);

const UploadCircularProgress = withStyles(theme => ({
    root: {
        color: '#0F467A',
        width:'100px !important',
        height:'100px !important',
        position:'absolute',
        [theme.breakpoints.between('xs','md')]: {
            width: '80px !important',
            height: '80px !important',
        }
    },
}))(CircularProgress);

const useStyles = makeStyles((theme) => ({
  myLabel: {
    color: '#0A0945',
    opacity: '0.6',
    transform: 'translate(14px, 14px) scale(1)', // Centered position
    transition: 'transform 0.2s ease-out, font-size 0.2s ease-out', // Smooth transition
    '&.Mui-focused': {
      transform: 'translate(14px, -6px) scale(0.75)', // Move label up and shrink
      fontWeight: 'bold',
      color: '#0A0945 !important',
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)', // Shrink when value is present
    },
  },

  _inputCl:{
    padding:'12px 14px',
    '&.Mui-focused': {
      fontWeight: 'bold',
      color: '#0A0945',
    },
  },

  focusedCl:{
    borderColor:'#0A0945 !important',
    '&.MuiOutlinedInput-notchedOutline': {
      borderColor: '#0A0945 !important',
    },
  },

  formDiv:{
    width:'60%',
    minWidth:"300px",
    marginInline:'auto',
    [theme.breakpoints.between('xs', 'md')]: {
      width:'100%',
    }
  },

  backdrop: {
    zIndex: 111125,
    color: '#fff',
  },

    photoDiv:{
        alignSelf: 'center',
        position: 'relative',
        border: '1px solid #C4C4C4',
        width: '100px',
        height: '100px',
        minWidth: '100px',
        minHeight: '100px',
        borderRadius: '50px',
        margin: '10px 0px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        cursor: 'pointer',
        "& span":{
            textTransform: 'capitalize',
            textAlign: 'center',
            color: '#0F467A',
            fontFamily: "'Poppins', sans-serif",
        },
        "& img":{
            width: '100%',
            height: '100%',
            borderRadius: '50px'
        },
        [theme.breakpoints.between('xs','md')]: {
            width: '80px',
            height: '80px',
        }
    },

    photoIcon:{
        position: 'absolute',
        bottom: '0px',
        right: '0px',
        color: '#0F467A',
    }
}));

const initialState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    description: '',
    password: '',
};

const TeamRegistration = () => {
  const { data } = useCompanyData();
  const { t } = data;

  useEffect(()=> {
    window.scrollTo(0, 0);
  },[])

  return (
    <div>
      <SimpleHeader
        data={data}
        title={t && t('companyPage.registration')}
      />
      <Details />
    </div>
  );
};

const Details = () => {
    const { data } = useCompanyData();
    const { t, isRTL, fontFamily, company, buttonColor, buttonFontColor } = data;
    const email_patt = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const companyId = company && company.id;
    const [message,setMessage] = useState('');
    const [alertType,setAlertType] = useState('');
    const [openAlert,setAlert] = useState(false);
    const [loading,setLoading] = useState(false);

    const classes = useStyles();
    const imageRef = useRef(null);
    const [formData, setFormData] = useState(initialState);
    const [uploadLoading,setUploadLoading] = useState(false);
    const [avatar,setAvatar] = useState(false);

    const showAlert = (message,alertType) => {
        setMessage(message);
        setAlertType(alertType);
        setAlert(true);
    };

    const closeAlert = () => {
        setMessage('');
        setAlertType('');
        setAlert(false);
    };

    const createAccount = () => {
        if(!formData?.firstName) {
        showAlert(t && t('teamRegister.alert.firstName'),'error')
        }
        else if(!formData?.lastName) {
        showAlert(t && t('teamRegister.alert.lastName'),'error')
        }
        else if(!email_patt.test(formData.email)) {
        showAlert(t && t('teamRegister.alert.email'),'error')
        }
        else if(!formData?.phone) {
        showAlert(t && t('new_company.alert_msg.phone'),'error')
        }
        else if(formData?.password.length < 6) {
        showAlert(t && t('user-manage.new-dialog.msg.password'),'error')
        }
        else{
            setLoading(true);
            const dataObj = { 
                avatar: avatar || '',
                companyId,
                email: formData?.email || '',
                enabled:false,
                firstName: formData?.firstName || '',
                lastName: formData?.lastName || '',
                introduction: formData?.description || '',
                isChange:true,
                selfRegister: true,
                lang:'en',
                permission:{
                    activity: true,
                    course: true,
                    report: true,
                    setting: true
                },
                phone: formData?.phone || '',
                position: '',
                roleType: 'professional'
            };

            firebase.auth().createUserWithEmailAndPassword(formData?.email, formData?.password) 
            .then((userCredential) => {
                const user = userCredential.user;
                dataObj.id = user.uid;
                db.ref(`PartnerTeams/${user?.uid}`).set(dataObj)
                .then(()=> {
                    setLoading(false);
                    setFormData(initialState);
                    setAvatar('');
                    showAlert(t && t('teamRegister.alert.success'),'success')
                })
                .catch(err=> {
                    setLoading(false);
                    showAlert(err.message,'error')
                })
            })
            .catch((error) => {
                setLoading(false);
                showAlert(error.message,'error')
            })
        }
    };

    const uploadAvatar = async e => { // upload banner to storage
        const companyId = company && company.id;
        if (e.target.files && e.target.files.length > 0) {
            let docFile = e?.target?.files[0];
            try{
                setUploadLoading(true);
                const { allowed, message } = await checkStorageLimit({companyId,fileSize: docFile?.size});
                if(allowed) {
                if(avatar) deleteStorageFile(avatar,`${companyId}%2FTeamMembers%2F`,`${companyId}/TeamMembers`);
                    let compressFile = await CompressImage(docFile);
                    let storageRef = firebase.storage().ref().child(`${companyId}/TeamMembers/${nanoid()}`);
                    storageRef.put(compressFile).then((url) => {
                    url.ref.getDownloadURL().then((cover) => {
                        setAvatar(cover);
                        setUploadLoading(false);
                    })
                    .catch(e => {
                        setMessage(e.message);
                        setAlertType('error');
                        setAlert(true);
                        setUploadLoading(false);
                    })
                    });
                }
                else if(!allowed && message) {
                    setMessage(message);
                    setAlertType('error');
                    setAlert(true);
                    setUploadLoading(false);
                }
            }
            catch(e){
                setMessage(e);
                setAlertType('error');
                setAlert(true);
                setUploadLoading(false);
            }
        }
    };

    const handleOpenFile = () => {
        imageRef.current.click();
    };

  return (
    <Box
      py={5}
      width="90%"
      m="auto"
      display="flex"
      flexDirection='column'
      justifyContent="center"
      flexWrap="wrap"
      mt="100px"
    >
      {/* get in touch form */}
      <Box className={classes.formDiv}>
        <Box
          pb={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
            <input type="file" id="file" ref={imageRef} onClick={(event)=> { event.target.value = null}}
                style={{display: "none"}} accept="image/*" onChange={uploadAvatar}
            />
            <div className={classes.photoDiv} onClick={handleOpenFile}>
                {avatar ? 
                <img src={avatar} alt='avatar'/>
                :
                <span style={{fontFamily}}>{t && t('booking.dialog.profile')}</span>
                }
                <AddCircle size='large' className={classes.photoIcon} />
                {uploadLoading ? <UploadCircularProgress thickness={1.2}/> : null }
            </div>
        </Box>

        {/* first / last name */}
        <Box
          pb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box width="45%">
            <TextField
              label={t && t("profile-comp.first")}
              InputLabelProps={{ className: classes.myLabel }}
              InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
              fullWidth
              variant="outlined"
              id="firstName"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
            />
          </Box>
          <Box width="45%">
            <TextField
              InputLabelProps={{ className: classes.myLabel }}
              InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
              fullWidth
              variant="outlined"
              id="lastName"
              label={t && t("profile-comp.last")}
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
            />
          </Box>
        </Box>
        
        {/* email / phone */}
        <Box
          pb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box width="45%">
            <TextField
              InputLabelProps={{ className: classes.myLabel }}
              InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
              fullWidth
              variant="outlined"
              id="email"
              label={t && t('reservations.email')}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </Box>
          <Box width="45%">
            <TextField
              InputLabelProps={{ className: classes.myLabel }}
              InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
              fullWidth
              variant="outlined"
              id="phone"
              label={t && t('reservations.phoneNumber')}
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
            />
          </Box>
        </Box>

        {/* password */}
        <Box pb={2}>
            <TextField
              InputLabelProps={{ className: classes.myLabel }}
              InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
              fullWidth
              type='password'
              variant="outlined"
              id="password"
              label={t && t('user-manage.new-dialog.password')}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
        </Box>

        {/* description */}
        <Box pb={2}>
          <TextField
            InputLabelProps={{ className: classes.myLabel }}
            InputProps={{classes:{input:classes._inputCl,notchedOutline:classes.focusedCl}}}
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            id="description"
            label={t && t("companyPage.description")}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </Box>
        <Box textAlign={isRTL === 'ar' ? 'left' : "right"}>
            <CustomButton
              style={{backgroundColor: buttonColor ? buttonColor : 'inherit',color:buttonFontColor ? buttonFontColor : 'inherit'}}
              onClick={createAccount}
              endIcon={
                data.isRTL === 'ar' ? (
                  <ArrowBack
                    style={{ fontSize: '1.2rem', margin: '0 0.2em' }}
                  />
                ) : (
                  <ArrowForward
                    style={{ fontSize: '1.2rem', margin: '0 0.2em' }}
                  />
                )
              }
            >
              {t && t('companyPage.send')}
            </CustomButton>
        </Box>
      </Box>

        <AlertMessage
          {...{
            alertType,message,snackOpen:openAlert,
            closeAlert
          }}
        />

        <Backdrop className={classes.backdrop} open={loading}>
          <ColorCircularProgress />
        </Backdrop>
    </Box>
  );
};

export default TeamRegistration;